/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { GtFinalizeBlocker } from './gtDerivations'
import type { GtSaveStatus, GtSettleResult } from './useGtDocument'

import { isApiError } from '@/api'
import { type FinalizeBlockerReason, getGtFinalizeConflict, getGtRefusal } from '@/api/internal'
import { useFinalizeAttributionGt } from '@/hooks/useAttributionGt'

export interface GtFinalizeError {
  message: string
  needsReload: boolean
  turnId: string | null
}

export type GtFinalizeTone = 'neutral' | 'saved' | 'unsaved' | 'saving'

type Phase =
  | { kind: 'idle' }
  | { kind: 'confirming' }
  | { kind: 'finalizing' }
  | { kind: 'success'; generation: number }
  | { kind: 'error'; error: GtFinalizeError }

export interface UseGtFinalizeArgs {
  meetingId: string
  saveStatus: GtSaveStatus
  blockers: GtFinalizeBlocker[]
  dirty: boolean
  baselineSha: string
  finalizedGeneration: number | null
  finalizedSha: string | null
  settle: () => Promise<GtSettleResult>
  onFinalized: () => void
}

export interface UseGtFinalizeResult {
  label: string
  tone: GtFinalizeTone
  error: GtFinalizeError | null
  canFinalize: boolean
  disabledReason: string | null
  /** Turn to jump to for the reason `disabledReason` names, when the blocker is turn-scoped. */
  disabledTurnId: string | null
  busy: boolean
  confirmOpen: boolean
  request: () => void
  cancel: () => void
  confirm: () => void
  dismissError: () => void
}

const plainError = (message: string): GtFinalizeError => ({ message, needsReload: false, turnId: null })
const reloadError = (message: string): GtFinalizeError => ({ message, needsReload: true, turnId: null })

export const BLOCKER_LABEL: Record<FinalizeBlockerReason, string> = {
  no_speaker: 'A turn has no speaker',
  undecided: 'A turn is still undecided',
  generic_speaker: 'A speaker still has a generic name (e.g. "Speaker 1")',
  key_collision: 'Two speakers have names that collide',
  empty_key: "A speaker's name can't be turned into a key",
  no_turns: 'There are no turns to finalize',
}

const COUNTED_BLOCKER_LABEL: Partial<Record<FinalizeBlockerReason, (count: number) => string>> = {
  no_speaker: (count) => `${count} turns have no speaker`,
  undecided: (count) => `${count} turns are still undecided`,
}

const describeBlockers = (first: GtFinalizeBlocker, all: GtFinalizeBlocker[]): string => {
  const counted = COUNTED_BLOCKER_LABEL[first.reason]
  if (!counted) return BLOCKER_LABEL[first.reason]
  const count = all.filter((blocker) => blocker.reason === first.reason).length
  return count > 1 ? counted(count) : BLOCKER_LABEL[first.reason]
}

export const describeFinalizeError = (error: unknown): GtFinalizeError => {
  const conflict = getGtFinalizeConflict(error)
  if (conflict) {
    switch (conflict.reason) {
      case 'generation_conflict':
        return reloadError(
          conflict.current_generation != null
            ? `Someone finalized gen ${conflict.current_generation} — reload`
            : 'Someone finalized a newer version — reload'
        )
      case 'legacy_schema':
        return plainError('This meeting has a legacy ground-truth bundle and needs migration before finalizing')
      case 'unchanged':
        return plainError(
          conflict.current_generation != null
            ? `Nothing changed since gen ${conflict.current_generation}`
            : 'Nothing changed since the last generation'
        )
      default:
        return reloadError('Someone else saved a newer version — reload')
    }
  }
  const refusal = getGtRefusal(error)
  if (refusal) {
    if (refusal.reason === 'incomplete_review') {
      const detail = refusal.detail as FinalizeBlockerReason | null
      return {
        message: detail ? BLOCKER_LABEL[detail] : 'This meeting still needs review before finalizing',
        needsReload: false,
        turnId: refusal.turn_id ?? null,
      }
    }
    if (refusal.reason === 'stale_artifact') return reloadError('The transcript changed since this review — reload')
    return plainError(`Couldn't finalize: ${refusal.reason}`)
  }
  if (isApiError(error)) {
    if (error.status === 503) return plainError('Finalizing is paused right now')
    if (error.status === 502) return plainError("Couldn't store the record — retry")
    if (error.status === 404) return reloadError('Nothing to finalize — reload')
  }
  return plainError("Couldn't finalize — retry")
}

export const useGtFinalize = ({
  meetingId,
  saveStatus,
  blockers,
  dirty,
  baselineSha,
  finalizedGeneration,
  finalizedSha,
  settle,
  onFinalized,
}: UseGtFinalizeArgs): UseGtFinalizeResult => {
  const { mutateAsync: finalizeGt } = useFinalizeAttributionGt(meetingId)
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const finalizedGenerationRef = useRef(finalizedGeneration)
  finalizedGenerationRef.current = finalizedGeneration
  const onFinalizedRef = useRef(onFinalized)
  onFinalizedRef.current = onFinalized

  // A reload after success re-derives the chip from the server; drop the optimistic label then.
  useEffect(() => {
    setPhase((current) => (current.kind === 'success' ? { kind: 'idle' } : current))
  }, [finalizedGeneration])

  const unchanged = finalizedGeneration != null && !dirty && baselineSha === finalizedSha

  const firstBlocker = blockers[0] ?? null

  const disabledReason =
    phase.kind === 'finalizing'
      ? null
      : saveStatus === 'readonly'
        ? 'Read-only'
        : saveStatus === 'conflict'
          ? 'Resolve the save conflict first'
          : saveStatus === 'error'
            ? 'Fix the save error first'
            : firstBlocker
              ? describeBlockers(firstBlocker, blockers)
              : null
  const disabledTurnId = disabledReason && firstBlocker ? firstBlocker.turnId : null
  const canFinalize = phase.kind !== 'finalizing' && disabledReason === null

  const request = useCallback(() => {
    setPhase((current) => (current.kind === 'finalizing' ? current : { kind: 'confirming' }))
  }, [])

  const cancel = useCallback(() => {
    setPhase((current) => (current.kind === 'confirming' ? { kind: 'idle' } : current))
  }, [])

  const dismissError = useCallback(() => {
    setPhase((current) => (current.kind === 'error' ? { kind: 'idle' } : current))
  }, [])

  const confirm = useCallback(() => {
    setPhase({ kind: 'finalizing' })
    void (async () => {
      const settled = await settle()
      if (settled.outcome !== 'saved') {
        setPhase({
          kind: 'error',
          error:
            settled.outcome === 'conflict'
              ? reloadError('Someone else saved a newer version — reload')
              : plainError("Couldn't save your latest edits — fix the save error, then retry"),
        })
        return
      }
      try {
        const response = await finalizeGt({
          expected_version: settled.version,
          base_generation: finalizedGenerationRef.current,
        })
        setPhase({ kind: 'success', generation: response.data.generation })
        onFinalizedRef.current()
      } catch (error: unknown) {
        setPhase({ kind: 'error', error: describeFinalizeError(error) })
      }
    })()
  }, [settle, finalizeGt])

  const { label, tone } = useMemo((): { label: string; tone: GtFinalizeTone } => {
    if (phase.kind === 'finalizing') return { label: 'Finalizing…', tone: 'saving' }
    if (phase.kind === 'success') return { label: `Finalized · gen ${phase.generation}`, tone: 'saved' }
    if (finalizedGeneration == null) return { label: 'Not finalized', tone: 'neutral' }
    if (unchanged) return { label: `Finalized · gen ${finalizedGeneration}`, tone: 'saved' }
    return { label: `Edited since gen ${finalizedGeneration}`, tone: 'unsaved' }
  }, [phase, finalizedGeneration, unchanged])

  return useMemo(
    () => ({
      label,
      tone,
      error: phase.kind === 'error' ? phase.error : null,
      canFinalize,
      disabledReason,
      disabledTurnId,
      busy: phase.kind === 'finalizing',
      confirmOpen: phase.kind === 'confirming',
      request,
      cancel,
      confirm,
      dismissError,
    }),
    [label, tone, phase, canFinalize, disabledReason, disabledTurnId, request, cancel, confirm, dismissError]
  )
}
